<template>
    <div
        class="modal"
        v-if="showProgramModal"
        aria-labelledby="add-program-modal"
        role="dialog"
        aria-modal="true"
    >
        <div class="modal-overlay" aria-hidden="true"></div>
        <div
            class="inner rounded-lg bg-white shadow border border-gray-200 relative"
            style="-webkit-transform: translate3d(0, 0, 0)"
        >
            <!-- Close button (X) positioned absolutely relative to main container -->
            <button
                type="button"
                class="absolute p-2 flex items-center justify-center gap-2 rounded-md transition-colors"
                style="right: 16px; top: 22px"
                @click.prevent="closeModal()"
                title="Close modal"
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="text-slate-500"
                >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
            </button>

            <!-- Modal header with title -->
            <div class="px-6 pt-6">
                <div>
                    <h3
                        class="text-[20px] font-medium text-[#1E293B] font-['Inter'] leading-7"
                        v-if="step !== 5"
                    >
                        Add program
                    </h3>
                    <p
                        v-if="step === 0 && rewards.users.length > 1"
                        class="text-sm font-normal leading-5 text-slate-600 mt-1"
                    >
                        Select user to add program to
                    </p>
                    <p
                        v-if="step === 1"
                        class="text-sm font-normal leading-5 text-slate-600 mt-1"
                    >
                        Select program type
                    </p>
                    <p
                        v-if="step === 2 && program.type === 'airline'"
                        class="text-sm font-normal leading-5 text-slate-600 mt-1"
                    >
                        Select airline program
                    </p>
                    <p
                        v-if="step === 2 && program.type === 'hotel'"
                        class="text-sm font-normal leading-5 text-slate-600 mt-1"
                    >
                        Select hotel program
                    </p>
                    <p
                        v-if="
                            step === 2 && program.type === 'flexible_currency'
                        "
                        class="text-sm font-normal leading-5 text-slate-600 mt-1"
                    >
                        Select flexible currency program
                    </p>
                    <p
                        v-if="step === 3"
                        class="text-sm font-normal leading-5 text-slate-600 mt-1"
                    >
                        Enter program balance
                    </p>
                    <p
                        v-if="step === 4"
                        class="text-sm font-normal leading-5 text-slate-600 mt-1"
                    >
                        Set redemption value
                    </p>
                </div>
            </div>

            <div class="modal-body items-start !py-8 !px-6">
                <div class="steps">
                    <transition name="fadeInOut">
                        <div
                            class="step"
                            v-show="step === 0 && rewards.users.length > 1"
                        >
                            <label
                                class="block mb-2 text-sm text-slate-600 font-medium font-['Inter'] leading-5"
                            >
                                User
                            </label>

                            <div class="relative mb-6">
                                <!-- Custom dropdown -->
                                <div
                                    class="relative w-full"
                                    v-click-outside="closeUserDropdown"
                                >
                                    <!-- Text field that simulates the select -->
                                    <div
                                        ref="userDropdown"
                                        class="form-select w-full py-2.5 pl-3 pr-10 appearance-none border rounded-md bg-white shadow-sm cursor-pointer flex items-center"
                                        :class="{
                                            'border-slate-200': !errors.some(
                                                (error) =>
                                                    error.message ===
                                                    'Please choose a user.'
                                            ),
                                            'border-[#EF4444] border-[1px]':
                                                errors.some(
                                                    (error) =>
                                                        error.message ===
                                                        'Please choose a user.'
                                                ),
                                        }"
                                        @click="toggleUserDropdown"
                                    >
                                        <div class="flex-shrink-0 mr-2">
                                            <IconProgram
                                                icon="people"
                                                class="w-5 h-5 text-slate-800"
                                            />
                                        </div>
                                        <span
                                            v-if="
                                                program.user &&
                                                program.user.name
                                            "
                                            class="truncate text-sm font-normal font-['Inter'] leading-5"
                                        >
                                            {{ program.user.name }}
                                        </span>
                                        <span
                                            v-else
                                            class="text-slate-500 truncate text-sm font-normal font-['Inter'] leading-5"
                                        >
                                            Select User
                                        </span>
                                    </div>

                                    <!-- Custom dropdown list -->
                                    <teleport to="body">
                                        <div
                                            v-if="showUserDropdown"
                                            class="fixed border border-slate-200 rounded-md bg-white shadow-md z-[9999]"
                                            style="
                                                border-radius: 8px;
                                                box-shadow:
                                                    0px 1px 3px 0px
                                                        rgba(0, 0, 0, 0.1),
                                                    0px 1px 2px 0px
                                                        rgba(0, 0, 0, 0.06);
                                                max-height: 250px;
                                                overflow-y: auto;
                                            "
                                            :style="{
                                                top: dropdownTop + 'px',
                                                left: dropdownLeft + 'px',
                                                width: dropdownWidth + 'px',
                                            }"
                                        >
                                            <div class="py-1">
                                                <div
                                                    v-for="(
                                                        user, i
                                                    ) in rewards.users"
                                                    :key="i"
                                                    class="dropdown-option py-2.5 pl-3 pr-4 cursor-pointer block whitespace-nowrap"
                                                    :class="{
                                                        'text-slate-800 bg-slate-50 hover:bg-slate-100':
                                                            user.id ===
                                                            program.user?.id,
                                                        'text-slate-600 hover:bg-slate-100':
                                                            user.id !==
                                                            program.user?.id,
                                                    }"
                                                    style="
                                                        width: 100%;
                                                        font-size: 14px;
                                                        font-weight: 500;
                                                    "
                                                    @click="selectUser(user)"
                                                    @touchend="selectUser(user)"
                                                >
                                                    <div
                                                        class="flex items-center"
                                                    >
                                                        <div
                                                            class="flex-shrink-0 mr-2"
                                                        >
                                                            <IconProgram
                                                                icon="people"
                                                                class="w-5 h-5 text-slate-800"
                                                            />
                                                        </div>
                                                        <span>
                                                            {{ user.name }}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </teleport>
                                </div>
                            </div>
                        </div>
                    </transition>
                    <transition name="fadeInOut">
                        <div class="step" v-show="step === 1">
                            <h4
                                class="text-sm text-slate-600 font-medium font-['Inter'] leading-5 mb-3"
                            >
                                Program type
                            </h4>
                            <div class="flex flex-wrap gap-4">
                                <label
                                    v-for="(
                                        programType, typeIndex
                                    ) in filteredProgramTypes"
                                    :key="'program-type-' + typeIndex"
                                    class="p-3 rounded-[8px] border border-solid cursor-pointer relative flex flex-col items-start gap-2 flex-1"
                                    :class="{
                                        'border-[#FD421D] border-2':
                                            programType.type === program.type,
                                        'border-slate-200':
                                            programType.type !== program.type,
                                    }"
                                >
                                    <!-- Selection circle in the top right corner -->
                                    <div
                                        class="w-5 h-5 rounded-full absolute top-2 right-2 flex items-center justify-center"
                                        :class="{
                                            'border-[#FD421D] border-[6px] bg-white':
                                                programType.type ===
                                                program.type,
                                            'border-slate-300 border-[1px] bg-white':
                                                programType.type !==
                                                program.type,
                                        }"
                                        style="box-sizing: border-box"
                                    ></div>

                                    <input
                                        type="radio"
                                        name="program_type"
                                        :value="programType.type"
                                        v-model="program.type"
                                        class="sr-only"
                                    />
                                    <!-- Icon based on program type -->
                                    <IconProgram
                                        :icon="programType.icon"
                                        class="w-5 h-5 text-slate-500 ml-0"
                                    />
                                    <span
                                        class="text-sm font-medium text-[#1E293B] font-['Inter'] leading-5"
                                    >
                                        {{ programType.label }}
                                    </span>
                                </label>
                            </div>
                        </div>
                    </transition>
                    <transition name="fadeInOut">
                        <div class="step" v-show="step === 2">
                            <!-- Program search -->
                            <div class="relative mb-6">
                                <div
                                    class="w-full py-2.5 pl-3 pr-3 appearance-none border border-[#E2E8F0] rounded-md bg-white shadow-sm flex items-center gap-2 focus-within:border-[#1E293B] focus-within:ring-1 focus-within:ring-[#1E293B]"
                                >
                                    <i class="fas fa-search text-slate-800"></i>
                                    <input
                                        type="text"
                                        v-model="programSearchQuery"
                                        placeholder="Search program"
                                        class="w-full outline-none border-none focus:ring-0 placeholder-slate-400 text-sm font-normal leading-5 !p-0 !m-1"
                                    />
                                    <button
                                        v-if="programSearchQuery"
                                        @click="programSearchQuery = ''"
                                        type="button"
                                        class="flex-shrink-0 text-[#64748B] hover:text-[#1E293B] transition-colors"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="16"
                                            height="16"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        >
                                            <line
                                                x1="18"
                                                y1="6"
                                                x2="6"
                                                y2="18"
                                            ></line>
                                            <line
                                                x1="6"
                                                y1="6"
                                                x2="18"
                                                y2="18"
                                            ></line>
                                        </svg>
                                    </button>
                                </div>
                            </div>

                            <!-- List of programs with separators -->
                            <div
                                class="programs-list overflow-y-auto pr-2 tooltip-hidden"
                                style="
                                    scrollbar-width: thin;
                                    height: 405px;
                                    position: relative;
                                "
                            >
                                <div
                                    v-if="
                                        filteredProgramChoices.length === 0 &&
                                        programSearchQuery !== ''
                                    "
                                    class="flex flex-col justify-center items-center gap-2"
                                    style="
                                        position: absolute;
                                        top: 50%;
                                        left: 50%;
                                        transform: translate(-50%, -50%);
                                        width: 100%;
                                    "
                                >
                                    <div
                                        class="flex w-6 h-6 justify-center items-center"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            class="w-6 h-6 text-slate-800"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                            stroke-width="1.5"
                                        >
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                            />
                                        </svg>
                                    </div>
                                    <p
                                        class="text-[#64748B] text-center font-['Inter'] text-[14px] font-normal leading-5"
                                    >
                                        No results found
                                    </p>
                                </div>
                                <div
                                    v-else-if="!canChooseProgramChoice"
                                    class="text-center py-8 text-slate-500"
                                >
                                    Sorry, there are no available programs. You
                                    might have already added these.
                                </div>
                                <template v-else>
                                    <label
                                        v-for="(
                                            programChoice, index
                                        ) in filteredProgramChoices"
                                        :key="
                                            'program-choice-' + programChoice.id
                                        "
                                        class="block relative cursor-pointer tooltip-fix"
                                        :class="{
                                            'opacity-50 cursor-not-allowed':
                                                !programChoice.active,
                                        }"
                                    >
                                        <div
                                            style="
                                                display: flex;
                                                padding: 16px 0px;
                                                align-items: center;
                                                gap: 16px;
                                                align-self: stretch;
                                                position: relative;
                                            "
                                            :class="{
                                                'bg-white':
                                                    program.program_id ===
                                                    programChoice.id,
                                            }"
                                            :data-program-active="
                                                programChoice.active
                                                    ? 'true'
                                                    : 'false'
                                            "
                                        >
                                            <div
                                                style="
                                                    width: 112px;
                                                    height: 48px;
                                                    padding: 6px 14px;
                                                    display: flex;
                                                    align-items: center;
                                                    justify-content: center;
                                                "
                                            >
                                                <img
                                                    :src="`${cdnUrl}/${programChoice.program_image?.path}/_small/${programChoice.program_image?.file_name}`"
                                                    :alt="programChoice.name"
                                                    style="
                                                        width: 84px;
                                                        height: 50px;
                                                        object-fit: contain;
                                                    "
                                                />
                                            </div>
                                            <span
                                                class="text-[14px] font-medium text-[#1E293B] font-['Inter'] leading-5"
                                            >
                                                {{ programChoice.name }}
                                            </span>

                                            <!-- Check icon when selected -->
                                            <div
                                                v-if="
                                                    program.program_id ===
                                                    programChoice.id
                                                "
                                                style="
                                                    position: absolute;
                                                    right: 16px;
                                                "
                                            >
                                                <svg
                                                    width="24"
                                                    height="24"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <g
                                                        clip-path="url(#clip0_26_9297)"
                                                    >
                                                        <path
                                                            fill-rule="evenodd"
                                                            clip-rule="evenodd"
                                                            d="M12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2ZM15.774 10.1333C16.1237 9.70582 16.0607 9.0758 15.6332 8.72607C15.2058 8.37635 14.5758 8.43935 14.226 8.86679L10.4258 13.5116L9.20711 12.2929C8.81658 11.9024 8.18342 11.9024 7.79289 12.2929C7.40237 12.6834 7.40237 13.3166 7.79289 13.7071L9.79289 15.7071C9.99267 15.9069 10.2676 16.0129 10.5498 15.9988C10.832 15.9847 11.095 15.8519 11.274 15.6333L15.774 10.1333Z"
                                                            fill="#FD421D"
                                                        />
                                                    </g>
                                                    <defs>
                                                        <clipPath
                                                            id="clip0_26_9297"
                                                        >
                                                            <rect
                                                                width="24"
                                                                height="24"
                                                                fill="white"
                                                            />
                                                        </clipPath>
                                                    </defs>
                                                </svg>
                                            </div>

                                            <!-- Added indicator for programs already added -->
                                            <div
                                                v-if="!programChoice.active"
                                                class="flex justify-center items-center gap-2 absolute top-5 right-4 z-50"
                                                style="opacity: 1 !important"
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    class="w-5 h-5 text-[#1E293B]"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    stroke="currentColor"
                                                    stroke-width="2"
                                                >
                                                    <path
                                                        stroke-linecap="round"
                                                        stroke-linejoin="round"
                                                        d="M5 13l4 4L19 7"
                                                    />
                                                </svg>
                                                <span
                                                    class="text-[#1E293B] font-['Inter'] text-[14px] font-medium leading-5"
                                                    @mouseenter="
                                                        showTooltip($event)
                                                    "
                                                    @mouseleave="hideTooltip"
                                                >
                                                    Added
                                                </span>
                                            </div>
                                        </div>
                                        <input
                                            type="radio"
                                            name="program_type"
                                            :value="programChoice.id"
                                            v-model="program.program_id"
                                            class="sr-only"
                                            v-if="programChoice.active"
                                        />
                                        <div
                                            class="border-b border-slate-200"
                                            v-if="
                                                index <
                                                filteredProgramChoices.length -
                                                    1
                                            "
                                        ></div>
                                    </label>
                                </template>
                            </div>
                        </div>
                    </transition>
                    <transition name="fadeInOut">
                        <div class="step" v-show="step === 3">
                            <!-- Main container -->
                            <div
                                style="
                                    display: flex;
                                    flex-direction: column;
                                    justify-content: center;
                                    align-items: flex-start;
                                    align-self: stretch;
                                "
                            >
                                <!-- Selected program section -->
                                <p
                                    class="text-[14px] font-medium text-[#475569] font-['Inter'] leading-5 mb-2"
                                >
                                    Program
                                </p>
                                <div class="flex items-center gap-4 mb-8">
                                    <div
                                        style="
                                            width: 112px;
                                            height: 48px;
                                            padding: 6px 14px;
                                            display: flex;
                                            align-items: center;
                                            justify-content: center;
                                        "
                                    >
                                        <img
                                            v-if="
                                                chosenProgram &&
                                                chosenProgram.program_image
                                            "
                                            :src="`${cdnUrl}/${chosenProgram.program_image?.path}/_small/${chosenProgram.program_image?.file_name}`"
                                            :alt="
                                                chosenProgram
                                                    ? chosenProgram.name
                                                    : ''
                                            "
                                            style="
                                                width: 84px;
                                                height: 50px;
                                                object-fit: contain;
                                            "
                                        />
                                    </div>
                                    <span
                                        class="text-[14px] font-medium text-[#1E293B] font-['Inter'] leading-5"
                                    >
                                        {{
                                            chosenProgram
                                                ? chosenProgram.name
                                                : ''
                                        }}
                                    </span>
                                </div>

                                <!-- Program balance section -->
                                <div
                                    style="
                                        display: flex;
                                        flex-direction: column;
                                        align-items: flex-start;
                                        align-self: stretch;
                                        width: 100%;
                                    "
                                >
                                    <p
                                        class="text-[#475569] text-[14px] font-medium font-['Inter'] leading-5 mb-2"
                                    >
                                        Program balance
                                    </p>
                                    <div class="relative w-full">
                                        <input
                                            type="text"
                                            placeholder="Program balance"
                                            v-model="formattedPoints"
                                            class="w-full border rounded py-2 px-3 focus:outline-none focus:border-slate-400"
                                            :class="{
                                                'border-slate-200':
                                                    !errors.some(
                                                        (error) =>
                                                            error.message ===
                                                            'Please enter your points.'
                                                    ),
                                                'border-[#EF4444] border-[1px]':
                                                    errors.some(
                                                        (error) =>
                                                            error.message ===
                                                            'Please enter your points.'
                                                    ),
                                            }"
                                            @keydown="clearError"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </transition>
                    <transition name="fadeInOut">
                        <div class="step" v-show="step === 4">
                            <img
                                v-if="chosenProgram"
                                :src="`${cdnUrl}/${chosenProgram.program_image?.path}/_small/${chosenProgram.program_image?.file_name}`"
                                :alt="chosenProgram.name"
                                class="mb-8 mx-auto"
                                style="max-height: 100px"
                            />
                            <h4 class="text-lg lg:text-2xl text-center mb-8">
                                Do You Have Elite Status With
                                {{ chosenProgram ? chosenProgram.name : '' }}?
                            </h4>
                            <select
                                class="form-select mx-auto max-w-md mb-5"
                                v-model="program.has_elite_status"
                            >
                                <option value=""></option>
                                <option value="Y">Yes</option>
                                <option value="N">No</option>
                            </select>
                            <div
                                class="flex flex-col"
                                v-if="program.has_elite_status === 'Y'"
                            >
                                <h4 class="text-2xl text-center mb-5">
                                    What Elite Status Do You Have?
                                </h4>
                                <select
                                    class="form-select mx-auto max-w-md"
                                    v-model="program.elite_status"
                                >
                                    <option value=""></option>
                                    <option value="gold">
                                        {{
                                            chosenProgram
                                                ? chosenProgram.name
                                                : ''
                                        }}
                                        Gold
                                    </option>
                                    <option value="platinum">
                                        {{
                                            chosenProgram
                                                ? chosenProgram.name
                                                : ''
                                        }}
                                        Platinum
                                    </option>
                                </select>
                            </div>
                        </div>
                    </transition>
                    <transition name="fadeInOut">
                        <div
                            class="step flex flex-col items-center justify-center"
                            v-show="step === 5"
                            style="height: 300px; position: relative"
                        >
                            <!-- Card with program image -->
                            <div
                                class="relative flex flex-col items-center z-10"
                            >
                                <!-- Card design -->
                                <div
                                    class="relative rounded-xl flex flex-col"
                                    style="
                                        width: 288px;
                                        height: 184px;
                                        flex-shrink: 0;
                                        border-radius: 12px;
                                        background:
                                            linear-gradient(
                                                90deg,
                                                rgba(248, 248, 249, 0) 40%,
                                                #f8f8f9 100%
                                            ),
                                            #fff;
                                        box-shadow:
                                            0px 7px 29px 0px
                                                rgba(100, 100, 111, 0.2),
                                            0px 1px 2px 0px rgba(0, 0, 0, 0.06);
                                        padding: 18px;
                                    "
                                >
                                    <!-- Background SVG -->
                                    <div
                                        class="absolute inset-0 overflow-hidden rounded-xl pointer-events-none"
                                        style="z-index: 1"
                                    >
                                        <IconProgram icon="background-vector" />
                                    </div>

                                    <!-- Top section with points, program name and image -->
                                    <div
                                        class="flex justify-between items-start w-full relative"
                                        style="z-index: 2"
                                    >
                                        <!-- Points and program name -->
                                        <div
                                            class="flex flex-col"
                                            style="max-width: calc(100% - 70px)"
                                        >
                                            <p
                                                class="font-['Inter']"
                                                style="
                                                    color: #1e293b;
                                                    font-size: 24px;
                                                    font-style: normal;
                                                    font-weight: 500;
                                                    line-height: 32px;
                                                "
                                            >
                                                {{ formattedPoints }}
                                            </p>
                                            <p
                                                class="font-['Inter'] break-words"
                                                style="
                                                    color: #64748b;
                                                    font-size: 14px;
                                                    font-style: normal;
                                                    font-weight: 400;
                                                    line-height: 20px;
                                                "
                                            >
                                                {{
                                                    chosenProgram
                                                        ? chosenProgram.name
                                                        : ''
                                                }}
                                            </p>
                                        </div>

                                        <!-- Program image -->
                                        <div>
                                            <img
                                                v-if="chosenProgram"
                                                :src="`${cdnUrl}/${chosenProgram.program_image?.path}/_small/${chosenProgram.program_image?.file_name}`"
                                                :alt="
                                                    chosenProgram
                                                        ? chosenProgram.name
                                                        : ''
                                                "
                                                style="
                                                    max-height: 50px;
                                                    width: auto;
                                                "
                                            />
                                        </div>
                                    </div>

                                    <!-- Spacer to push content to bottom -->
                                    <div
                                        class="flex-grow"
                                        style="z-index: 2"
                                    ></div>

                                    <!-- Bottom section with program type label -->
                                    <div
                                        class="flex justify-between items-end w-full mt-auto"
                                        style="z-index: 2"
                                    >
                                        <!-- Program type label -->
                                        <div
                                            style="
                                                display: inline-flex;
                                                padding: 2px 12px;
                                                justify-content: center;
                                                align-items: center;
                                                border-radius: 999px;
                                                border: 0.793px solid
                                                    var(--Border-Light, #e2e8f0);
                                            "
                                        >
                                            <span
                                                style="
                                                    color: var(
                                                        --text-icons-tertiary,
                                                        #64748b
                                                    );
                                                    font-family: Inter;
                                                    font-size: 12px;
                                                    font-style: normal;
                                                    font-weight: 400;
                                                    line-height: 16px;
                                                "
                                            >
                                                {{
                                                    program.type === 'airline'
                                                        ? 'Airline'
                                                        : program.type ===
                                                            'hotel'
                                                          ? 'Hotel'
                                                          : 'Flexible Currency'
                                                }}
                                            </span>
                                        </div>

                                        <!-- Spacer for right side balance -->
                                        <div></div>
                                    </div>

                                    <!-- Check icon in the bottom right corner -->
                                    <div
                                        class="absolute -bottom-4 right-4 flex items-center justify-center h-10 w-10"
                                        style="
                                            display: inline-flex;
                                            padding: 8px;
                                            align-items: center;
                                            border-radius: 2397.6px;
                                            background: #34c759;
                                            box-shadow:
                                                0px 1px 2px 0px
                                                    rgba(0, 0, 0, 0.05),
                                                0px 1px 2px 0px
                                                    rgba(0, 0, 0, 0.06);
                                            z-index: 2;
                                        "
                                    >
                                        <svg
                                            class="h-6 w-6 text-white"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke-width="2"
                                            stroke="currentColor"
                                        >
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                d="M4.5 12.75l6 6 9-13.5"
                                            />
                                        </svg>
                                    </div>
                                </div>
                            </div>

                            <!-- Confirmation message -->
                            <div
                                class="text-slate-800 mt-5 mb-2 z-10 relative"
                                style="
                                    font-size: 18px;
                                    font-weight: 500;
                                    line-height: 28px;
                                "
                            >
                                Program successfully added
                            </div>

                            <!-- SVG Background -->
                            <div class="svg-background">
                                <IconProgram icon="program-background" />
                            </div>
                        </div>
                    </transition>
                </div>
            </div>
            <footer
                class="modal-footer flex !flex-row flex-wrap justify-between items-center px-6 !pt-0 lg:!relative"
            >
                <Errors :errors="errors" class="mb-2" />
                <div
                    class="w-full mb-4 flex items-center justify-between"
                    v-if="step !== 5"
                >
                    <div class="w-1/4">
                        <button
                            v-if="actualStep > 0"
                            type="button"
                            class="px-4 py-2.5 border border-slate-200 rounded bg-white shadow-sm text-[#1E293B] font-['Inter'] text-[14px] font-medium leading-5"
                            @click.prevent="prevStep()"
                        >
                            Back
                        </button>
                    </div>
                    <div class="w-1/2">
                        <ul
                            class="w-full flex justify-center items-center gap-2"
                        >
                            <li
                                v-for="stepIndex in totalSteps"
                                :key="'step-' + stepIndex"
                                class="w-6 h-1 rounded-sm"
                                :class="{
                                    'bg-slate-800':
                                        stepIndex === actualStep + 1,
                                    'bg-slate-200':
                                        stepIndex !== actualStep + 1,
                                }"
                            ></li>
                        </ul>
                    </div>
                    <div class="w-1/4 flex gap-2 justify-end">
                        <button
                            type="button"
                            class="px-4 py-2.5 border border-slate-200 rounded bg-white shadow-sm text-[#1E293B] font-['Inter'] text-[14px] font-medium leading-5"
                            @click.prevent="closeModal()"
                        >
                            Cancel
                        </button>
                        <ActionButton
                            v-if="canSaveStep"
                            @clicked="addReward"
                            label="Add"
                            :loading="saveProgramBtnAction"
                            classes="text-white"
                            style="
                                display: flex;
                                padding: 10px 16px;
                                justify-content: center;
                                align-items: center;
                                gap: 8px;
                                border-radius: var(--radius-md-base, 4px);
                                background: var(--Background-Darkest, #1e293b);
                                box-shadow:
                                    0px 1px 2px 0px rgba(0, 0, 0, 0.05),
                                    0px 1px 2px 0px rgba(0, 0, 0, 0.06);
                            "
                        />
                        <button
                            v-if="canNextStep"
                            type="button"
                            class="px-4 py-2.5 rounded bg-slate-800 text-white shadow-sm"
                            @click.prevent="nextStep()"
                            :disabled="false"
                        >
                            Next
                        </button>
                    </div>
                </div>

                <div v-if="step === 5" class="w-full flex justify-end gap-4">
                    <button
                        type="button"
                        class="border border-slate-200 rounded"
                        style="
                            display: flex;
                            padding: 10px 16px;
                            justify-content: center;
                            align-items: center;
                            gap: 8px;
                            border-radius: 4px;
                            background: var(--Background-White, #fff);
                            box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);
                        "
                        @click.prevent="addAnotherReward()"
                    >
                        Add Another Program
                    </button>

                    <button
                        type="button"
                        class="text-white"
                        style="
                            display: flex;
                            padding: 10px 16px;
                            justify-content: center;
                            align-items: center;
                            gap: 8px;
                            border-radius: 4px;
                            background: var(--Primary-10x-Orange, #fd421d);
                            box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);
                        "
                        @click.prevent="closeModal()"
                    >
                        Finish
                    </button>
                </div>
            </footer>
        </div>
    </div>
    <teleport to="body">
        <div
            v-if="tooltipVisible"
            class="fixed inline-flex p-[4px_8px] flex-col items-start rounded-[8px] bg-[#1E293B] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]"
            style="z-index: 999999; pointer-events: none"
            :style="tooltipStyle"
        >
            <span
                class="font-['Inter'] text-[14px] font-normal leading-[20px] text-white"
            >
                Program already added
            </span>
        </div>
    </teleport>
</template>
<script>
import { mapState, mapActions } from 'pinia';
import { usePointsStore } from '@/store/points';
import IconProgram from '@/components/ui/icon/icon-program.vue';
import ActionButton from '@/components/action-button.vue';
import Errors from '@/components/errors.vue';
import vClickOutside from 'click-outside-vue3';
import { notify } from 'notiwind';

export default {
    name: 'AddRewardModal',

    components: {
        IconProgram,
        ActionButton,
        Errors,
    },
    directives: {
        ClickOutside: vClickOutside.directive,
    },
    data() {
        return {
            step: 0,
            errors: [],
            saveProgramBtnAction: false,
            programSearchQuery: '',
            showUserDropdown: false,
            dropdownTop: 0,
            dropdownLeft: 0,
            dropdownWidth: 0,
            tooltipVisible: false,
            tooltipStyle: {},
            program: {
                user: null,
                type: null,
                has_elite_status: null,
                elite_status: null,
                points: null,
                program_id: null,
            },
            perPage: 12,
            currentPage: 1,
            startIndex: 0,
            endIndex: 9,
            programTypes: [
                {
                    type: 'airline',
                    label: 'Airline',
                    icon: 'airline',
                    active: true,
                },
                {
                    type: 'hotel',
                    label: 'Hotel',
                    icon: 'hotel',
                    active: true,
                },
                {
                    type: 'flexible_currency',
                    label: 'Flexible Currency',
                    icon: 'flexible_currency',
                    active: true,
                },
            ],
        };
    },
    watch: {
        showProgramModal: {
            handler(value) {
                if (value) {
                    // Ensure the modal always starts at the correct step when opened
                    if (this.rewards.users && this.rewards.users.length > 1) {
                        this.step = 0; // Show user selection when there are multiple users
                    } else if (
                        this.rewards.users &&
                        this.rewards.users.length === 1
                    ) {
                        this.step = 1; // Go to step 1 if there's only one user
                        this.program.user = this.rewards.users[0];
                    }
                }
            },
            immediate: true,
        },
        user() {
            if (this.user.hasOwnProperty('id')) {
                this.program.user = this.rewards.users.find((u) => {
                    return u.id === this.user.id;
                });
            }
        },
    },
    computed: {
        ...mapState(usePointsStore, [
            'programs',
            'rewards',
            'reward',
            'user',
            'addRewardUser',
            'showProgramModal',
            'showEditProgramModal',
        ]),
        cdnUrl() {
            return window.cdnUrl;
        },
        filteredProgramTypes() {
            return this.programTypes;
        },
        chosenProgramType() {
            return this.programTypes.filter((obj) => {
                return obj.type === this.program.type;
            })[0];
        },
        chosenProgram() {
            return this.programs.filter((obj) => {
                return obj.id === parseInt(this.program.program_id);
            })[0];
        },
        programChoices() {
            this.programs.forEach((item) => {
                item.active =
                    item.type === this.program.type &&
                    this.userPrograms.includes(item.id)
                        ? false
                        : true;
            });

            return this.programs.filter((obj) => {
                return obj.type === this.program.type;
            });
        },
        canChooseProgramChoice() {
            let canChoose = false;
            this.programChoices.forEach((item) => {
                if (item.active) {
                    console.log('found active');
                    canChoose = true;
                }
            });
            return canChoose;
        },
        filteredProgramChoices() {
            let programChoices = this.programChoices;

            // Filter by search term if exists
            if (this.programSearchQuery.trim() !== '') {
                const searchTerm = this.programSearchQuery.toLowerCase();
                programChoices = programChoices.filter((program) =>
                    program.name.toLowerCase().includes(searchTerm)
                );
            }

            // Return all results without pagination
            return programChoices;
        },
        userPrograms() {
            let chosenUser = this.program.user;
            let programs = [];

            if (chosenUser && chosenUser.programs) {
                chosenUser.programs.forEach((program) => {
                    programs.push(program.id);
                });
            }

            return programs;
        },
        milesPoints() {
            let label = 'Points';
            if (this.chosenProgram) {
                if (this.chosenProgram.type === 'airline') {
                    label = 'Miles';
                }
            }
            return label;
        },
        canSaveStep() {
            // TODO Skipping step 4 for now
            if (this.step === 3) {
                return true;
            }
            /*if (this.program.type === "flexible_currency" && this.step === 3) {
                return true;
            }
            if (this.program.type !== "flexible_currency" && this.step === 4) {
                return true;
            }*/
            return false;
        },
        canNextStep() {
            // TODO Skipping step 4 for now
            /*if (this.program.type === "flexible_currency" && this.step === 3) {
                return false;
            }
            if (this.step === 4 || this.step === 5) {
                return false;
            }*/
            if (this.step === 3 || this.step === 4) {
                return false;
            }
            return true;
        },
        actualStep() {
            return this.rewards.users.length === 1 ? this.step - 1 : this.step;
        },
        totalSteps() {
            return 4; // Always 4 steps in the progress bar
        },
        formattedPoints: {
            get() {
                if (!this.program.points) return '';
                // Format number with commas
                return this.program.points
                    .toString()
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            },
            set(value) {
                // Remove all non-digit characters and store in program.points
                const numericValue = value.replace(/[^\d]/g, '');
                this.program.points = numericValue || null;
            },
        },
    },
    methods: {
        ...mapActions(usePointsStore, [
            'getRewards',
            'setProgramModal',
            'setEditProgramModal',
            'showProgramModalAction',
        ]),
        addReward() {
            this.validateStep();

            if (this.errors.length === 0) {
                this.saveProgramBtnAction = true;
                let data = Object.assign({}, this.program);

                if (this.program.user && this.program.user.id) {
                    data.user = {
                        id: this.program.user.id,
                        name: this.program.user.name || '',
                    };
                } else {
                    data.user = null;
                }

                ApiService.apiCall('/rewards', 'post', data)
                    .then((response) => {
                        setTimeout(() => {
                            this.saveProgramBtnAction = false;
                            this.nextStep();
                        }, 500);
                        if (import.meta.env.VITE_APP_ENV === 'production') {
                            woopra.track('program-added-my10x', {
                                program: this.chosenProgram.name,
                                balance: this.program.points,
                                uuid: localStorage.getItem('uuid'),
                            });
                        }
                        let rewardsMessage = response.data.has_rewards
                            ? 'Your rewards will sync automatically each night.'
                            : 'You will need to manually enter your rewards balance.';

                        notify({
                            group: 'success',
                            title: 'Success',
                            text:
                                'Your program has been created. ' +
                                rewardsMessage,
                        });
                    })
                    .catch((error) => {
                        this.$root.handleHttpStatus(error);
                        this.saveProgramBtnAction = false;
                    });
            }
        },
        addAnotherReward() {
            this.resetProgram();
        },
        resetProgram() {
            if (this.rewards.users.length === 1) {
                this.step = 1;
                this.program = {
                    user: this.rewards.users[0],
                    type: null,
                    has_elite_status: null,
                    elite_status: null,
                    points: null,
                    program_id: null,
                };
            } else {
                this.step = 0;
                this.program = {
                    user: null,
                    type: null,
                    has_elite_status: null,
                    elite_status: null,
                    points: null,
                    program_id: null,
                };
            }
            this.progressPercent = 0;
        },
        nextStep() {
            // If we're on step 0 and no user has been selected, show error
            if (
                this.step === 0 &&
                (!this.program.user || !this.program.user.id)
            ) {
                this.errors = [{ message: 'Please choose a user.' }];
                return;
            }

            // If we're on step 1 and no program type has been selected, show error
            if (this.step === 1 && !this.program.type) {
                this.errors = [{ message: 'Please select a program type.' }];
                return;
            }

            // If we're on step 2 and no program has been selected, show error
            if (this.step === 2 && !this.program.program_id) {
                this.errors = [{ message: 'Please select a program.' }];
                return;
            }

            // If we're on step 3 and no points have been entered, show error
            if (this.step === 3 && !this.program.points) {
                this.errors = [{ message: 'Please enter your points.' }];
                return;
            }

            this.errors = []; // Clear errors when advancing
            this.step++;
            // TODO Skipping step 4 for now
            if (this.step === 4) {
                this.step = 5;
            }
            this.setProgress();
            if (this.step === 4 && this.program.type === 'flexible_currency') {
                this.step = this.step + 1;
                this.setProgress();
            }
        },
        prevStep() {
            this.step--;
            // TODO Skipping step 4 for now
            if (this.step === 4) {
                this.step = 3;
            }
            this.setProgress();
        },
        validateStep() {
            this.errors = [];
            switch (this.step) {
                case 0:
                    if (!this.program.user) {
                        this.errors.push({ message: 'Please choose a user.' });
                    }
                    break;
                case 1:
                    if (!this.program.type) {
                        this.errors.push({
                            message: 'Please choose a program type.',
                        });
                    }
                    break;
                case 2:
                    if (!this.program.program_id) {
                        this.errors.push({
                            message: 'Please choose a program.',
                        });
                    }
                    break;
                case 3:
                    if (!this.program.points) {
                        this.errors.push({
                            message: 'Please enter your points.',
                        });
                    }
                    break;
                case 4:
                    if (this.program.type !== 'flexible_currency') {
                        if (
                            this.program.has_elite_status &&
                            this.program.has_elite_status === 'Y' &&
                            !this.program.elite_status
                        ) {
                            this.errors.push({
                                message: 'Please enter your elite status.',
                            });
                        }
                    }
                    break;
            }
        },
        setProgress() {
            // If we are on step 5 (congratulations), keep the progress at 100%
            if (this.step === 5) {
                this.progressPercent = 100;
            } else {
                this.progressPercent =
                    (this.actualStep / this.totalSteps) * 100;
            }
        },
        closeModal() {
            this.resetProgram();
            this.errors = []; // Clear errors when closing the modal
            this.setProgramModal(false);
            this.getRewards();
        },
        toggleUserDropdown() {
            this.showUserDropdown = !this.showUserDropdown;
            if (this.showUserDropdown) {
                const dropdownRect =
                    this.$refs.userDropdown.getBoundingClientRect();
                this.dropdownTop = dropdownRect.bottom + window.scrollY + 5;
                this.dropdownLeft = dropdownRect.left + window.scrollX;
                this.dropdownWidth = dropdownRect.width;
            }
        },
        selectUser(user) {
            this.program.user = user;
            this.showUserDropdown = false;
            // Clear the user selection error if it exists
            this.errors = this.errors.filter(
                (error) => error.message !== 'Please choose a user.'
            );
        },
        closeUserDropdown() {
            this.showUserDropdown = false;
        },
        clearError() {
            this.errors = [];
        },
        showTooltip(event) {
            this.tooltipVisible = true;
            const target = event.target;
            const rect = target.getBoundingClientRect();
            this.tooltipStyle = {
                top: `${rect.top - 40}px`,
                left: `${rect.left + rect.width / 2}px`,
                transform: 'translateX(-50%)',
            };
        },
        hideTooltip() {
            this.tooltipVisible = false;
        },
    },
    created() {},
    mounted() {
        // Ensure the modal always starts at the correct step when opened
        if (this.rewards.users && this.rewards.users.length > 1) {
            this.step = 0; // If there are multiple users, start at step 0 to choose a user
        } else {
            this.step = 1; // If there's only one user, start at step 1
            if (this.rewards.users && this.rewards.users.length === 1) {
                this.program.user = this.rewards.users[0];
            }
        }

        if (this.user.hasOwnProperty('id')) {
            this.program.user = this.rewards.users.find((u) => {
                return u.id === this.user.id;
            });
        }
    },
};
</script>
<style lang="scss" scoped>
.steps {
    position: relative;

    .programs-list {
        &::-webkit-scrollbar {
            width: 6px;
        }

        &::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 3px;
        }

        &::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 3px;
        }

        &::-webkit-scrollbar-thumb:hover {
            background: #a0aec0;
        }

        label {
            .border-b {
                border-color: #e2e8f0;
            }
        }
    }

    height: 100%;
}
.step {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
}
.program-types {
    @apply grid grid-cols-1 gap-4 md:grid-cols-3 justify-items-center;

    label {
        @apply flex
        justify-center
        items-center
        flex-col
        border-gray-300 border
        rounded-md
        font-bold
        text-sm
        p-2
        md:p-6
        mb-3
        text-center
        font-normal
        w-48
        cursor-pointer;
        //min-width: 220px;

        &.active {
            @apply bg-gray-100;
            .option-icon {
                @apply bg-gray-700;
            }
            @apply border-orange;
            i {
                @apply text-orange;
            }
        }
    }
}
.programs {
    /*display: flex;
    justify-content: space-evenly;
    flex-wrap: wrap;*/

    @apply grid grid grid-cols-2 gap-2 md:grid-cols-4 md:gap-4 justify-items-center mb-3;

    label {
        @apply flex
        justify-center
        items-center
        flex-col
        border-gray-300 border
        rounded-md
        font-bold
        text-xs
        md:text-sm
        p-4
        md:p-6
        max-w-[12rem]
        w-full
        text-center

        cursor-pointer;
        //min-width: 220px;

        &.active {
            @apply bg-gray-100;
            .option-icon {
                @apply bg-gray-700;
            }
            @apply border-orange;
            i {
                @apply text-orange;
            }
        }
        img {
            @apply object-contain;
        }
    }
}

select {
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
    background-position: right 0.5rem center;
    background-repeat: no-repeat;
    background-size: 1.5em 1.5em;
}

/* Style for the dropdown when it is deployed */
select:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(79, 70, 229, 0.2);
}

/* Customizing the dropdown list */
select option {
    padding: 8px;
    background-color: white;
}

.svg-background {
    position: absolute;
    left: 0;
    right: 0;
    bottom: -100px;
    width: 100%;
    height: 100%;
    z-index: 0;
    overflow: visible;
    display: flex;
    justify-content: center;
}

.card-container {
    position: relative;
    z-index: 10;
    margin-bottom: 20px;
}
</style>
